import React, { useEffect, useRef, useState } from 'react';
import { blePrintReceipt, bleOpenCashDrawer, bleClearLastPrinter } from '../../utils/blePrinter';
import { isCashDrawerEnabled } from './BillingSettings';

const round2 = (n) => Math.round(((n || 0) + Number.EPSILON) * 100) / 100;

const InvoicePrint = ({ order, mergedOrderNumbers, restaurant, billingSettings, onClose }) => {
  const invoiceRef = useRef(null);
  const [bleStatus, setBleStatus] = useState('idle'); // idle | connecting | done | offline | error
  const [bleError, setBleError] = useState('');
  const [blePrinterName, setBlePrinterName] = useState('');

  const isCash = order.payment_method === 'cash';

  const handleBlePrint = async () => {
    setBleStatus('connecting');
    setBleError('');
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
    try {
      const openDrawer = isCash && isCashDrawerEnabled();
      const printerName = await blePrintReceipt({ order, mergedOrderNumbers, restaurant, billingSettings: billingSettings, openDrawer });
      if (printerName) setBlePrinterName(printerName);
      setBleStatus('done');
      setTimeout(() => setBleStatus('idle'), 5000);
    } catch (e) {
      if ((e.name === 'NotFoundError' || e.message?.includes('cancelled')) && !window.menuronDesktop?.isDesktop) {
        setBleStatus('idle');
      } else if (e.message?.includes('timed out')) {
        setBleStatus('offline');
      } else {
        setBleError(e.message || e.name || 'Print failed');
        setBleStatus('error');
        setTimeout(() => setBleStatus('idle'), 8000);
      }
    }
  };

  const handleOpenDrawer = async () => {
    try { await bleOpenCashDrawer(); } catch (_) {}
  };

  const handleChangePrinter = () => {
    bleClearLastPrinter();
    setBleStatus('idle');
  };

  const curr      = restaurant?.default_currency || '₹';
  const fmt       = (n) => `${curr}${round2(n).toFixed(2)}`;
  const gstin     = billingSettings?.gstin || '';
  const isGst     = !!(gstin && (order.gst_amount > 0 || order.applied_gst_percent > 0));
  const docTitle  = isGst ? 'TAX INVOICE' : 'RECEIPT';
  const halfRate  = round2((order.applied_gst_percent || 0) / 2);
  const cgst      = round2((order.gst_amount || 0) / 2);
  const sgst      = round2((order.gst_amount || 0) / 2);
  const gross     = round2((order.subtotal || 0) + (order.discount_amount || 0));
  const showTaxableRow = ((order.discount_amount || 0) > 0 || (order.gst_amount || 0) > 0 || (order.service_charge_amount || 0) > 0);

  const dateObj = new Date(order.created_at);
  const dateStr = dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = dateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  const paperWidth = billingSettings?.thermal_paper_width || 80;
  const sheetWidth = paperWidth - 4; // 2mm margin each side

  // Inject print CSS sized for the restaurant's thermal paper width
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'menuron-print-style';
    style.textContent = `
      @media print {
        @page {
          size: ${paperWidth}mm auto;
          margin: 0;
        }
        body > * { visibility: hidden !important; }
        #menuron-invoice-overlay {
          visibility: visible !important;
          position: fixed !important;
          inset: 0 !important;
          background: white !important;
          padding: 0 !important;
          display: flex !important;
          justify-content: flex-start !important;
          align-items: flex-start !important;
        }
        #menuron-invoice-overlay * { visibility: visible !important; }
        #menuron-invoice-no-print { display: none !important; }
        #menuron-invoice-sheet {
          width: ${sheetWidth}mm !important;
          max-width: ${sheetWidth}mm !important;
          margin: 0 !important;
          padding: 0 !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          font-size: 11px !important;
          line-height: 1.4 !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById('menuron-print-style');
      if (el) el.remove();
    };
  }, [paperWidth]);

  const handlePrint = () => window.print();

  const handleSendWhatsApp = () => {
    const phone = (order.customer_phone || '').replace(/\D/g, '');
    if (!phone) return;
    const link = `https://menuron.prismatic-a.com/invoice/${order.id}`;
    const text = encodeURIComponent(`Your bill is ready.\nInvoice: ${order.invoice_number}\nView & download: ${link}`);
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  const handleDownloadPdf = () => {
    import('html2pdf.js').then(({ default: html2pdf }) => {
      html2pdf()
        .set({
          margin: 8,
          filename: `${order.invoice_number || 'receipt'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a5', orientation: 'portrait' },
        })
        .from(invoiceRef.current)
        .save();
    });
  };

  // ─── Styles ────────────────────────────────────────────────────────────────
  const overlay = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 2000, padding: '20px', overflowY: 'auto',
  };
  const sheet = {
    background: 'white', width: '420px', maxWidth: '100%',
    borderRadius: '4px', fontFamily: "'Courier New', Courier, monospace",
    fontSize: '13px', color: '#111', lineHeight: '1.5',
    boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
  };
  const section = { padding: '14px 18px', borderBottom: '1px dashed #ccc' };
  const row = { display: 'flex', justifyContent: 'space-between', padding: '2px 0' };
  const totalRow = {
    display: 'flex', justifyContent: 'space-between',
    padding: '8px 0 4px', borderTop: '2px solid #111',
    fontWeight: '800', fontSize: '15px', marginTop: '4px',
  };
  const metaRow = { display: 'flex', gap: '6px', padding: '2px 0', fontSize: '12px', color: '#333' };

  return (
    <div id="menuron-invoice-overlay" style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>

      {/* Action bar (hidden on print) */}
      <div id="menuron-invoice-no-print" style={{
        position: 'fixed', top: '16px', right: '20px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexWrap: 'wrap', gap: '8px', zIndex: 2100,
      }}>
        <button onClick={handleDownloadPdf} style={{
          background: '#1e40af', color: 'white', border: 'none', borderRadius: '8px',
          padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
        }}>
          Download PDF
        </button>
        <button onClick={handlePrint} style={{
          background: '#111', color: 'white', border: 'none', borderRadius: '8px',
          padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
        }}>
          Print
        </button>
        {order.customer_phone && (
          <button onClick={handleSendWhatsApp} style={{
            background: '#25D366', color: 'white', border: 'none', borderRadius: '8px',
            padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
          }}>
            Send on WhatsApp
          </button>
        )}
        {bleStatus === 'offline' && (
          <div style={{ color: '#fbbf24', fontSize: '0.82rem', fontWeight: '600' }}>
            Printer offline — is it on?
          </div>
        )}
        <button
          onClick={handleBlePrint}
          disabled={bleStatus === 'connecting'}
          style={{
            background: bleStatus === 'done' ? '#16a34a' : bleStatus === 'error' || bleStatus === 'offline' ? '#dc2626' : '#7c3aed',
            color: 'white', border: 'none', borderRadius: '8px',
            padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem',
            cursor: bleStatus === 'connecting' ? 'not-allowed' : 'pointer',
            opacity: bleStatus === 'connecting' ? 0.7 : 1,
          }}
        >
          {bleStatus === 'connecting' ? 'Connecting...' :
           bleStatus === 'done'       ? `Printed! ${blePrinterName ? '→ ' + blePrinterName : ''}` :
           bleStatus === 'offline' || bleStatus === 'error' ? 'Retry' :
           'Bluetooth Print'}
        </button>
        {isCashDrawerEnabled() && (
          <button onClick={handleOpenDrawer} style={{
            background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.5)',
            borderRadius: '8px', padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
          }}>
            Open Drawer
          </button>
        )}
        <button onClick={handleChangePrinter} style={{
          background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.5)',
          borderRadius: '8px', padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
        }}>
          Change Printer
        </button>
        {bleStatus === 'error' && bleError && (
          <div style={{ color: '#fca5a5', fontSize: '0.75rem', maxWidth: '180px' }}>
            {bleError}
          </div>
        )}
        <button onClick={onClose} style={{
          background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.5)',
          borderRadius: '8px', padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
        }}>
          Close
        </button>

      </div>

      {/* Invoice sheet */}
      <div id="menuron-invoice-sheet" ref={invoiceRef} style={{ ...sheet, marginTop: '60px', marginBottom: '20px' }}>

        {/* Header */}
        <div style={{ ...section, textAlign: 'center', borderBottom: '2px solid #111' }}>
          {billingSettings?.print_logo && restaurant?.logo_url && (
            <img
              src={restaurant.logo_url}
              alt={restaurant.name}
              style={{ maxHeight: '64px', maxWidth: '160px', objectFit: 'contain', marginBottom: '8px', display: 'block', margin: '0 auto 8px' }}
            />
          )}
          <div style={{ fontSize: '17px', fontWeight: '800', letterSpacing: '2px', marginBottom: '6px' }}>
            {docTitle}
          </div>
          <div style={{ fontSize: '15px', fontWeight: '700' }}>{restaurant?.name}</div>
          {billingSettings?.print_tagline && restaurant?.tagline && (
            <div style={{ fontSize: '11px', color: '#555', fontStyle: 'italic', marginTop: '2px' }}>{restaurant.tagline}</div>
          )}
          {restaurant?.address && (
            <div style={{ fontSize: '11px', color: '#444', marginTop: '2px' }}>{restaurant.address}</div>
          )}
          {restaurant?.contact_phone && (
            <div style={{ fontSize: '11px', color: '#444' }}>Ph: {restaurant.contact_phone}</div>
          )}
          {isGst && gstin && (
            <div style={{ fontSize: '12px', fontWeight: '700', marginTop: '6px' }}>
              GSTIN: {gstin}
            </div>
          )}
        </div>

        {/* Meta */}
        <div style={section}>
          <div style={metaRow}><span style={{ color: '#555', minWidth: '110px' }}>Invoice No</span><span style={{ fontWeight: '700' }}>{order.invoice_number || '—'}</span></div>
          {order.order_number && (
            <div style={{ ...metaRow, alignItems: 'flex-start' }}>
              <span style={{ color: '#555', minWidth: '110px', flexShrink: 0 }}>Order No</span>
              <span style={{ fontWeight: '700', display: 'flex', flexWrap: 'wrap', gap: '0 4px' }}>
                {mergedOrderNumbers?.length > 0
                  ? [order.order_number, ...mergedOrderNumbers].map((num, i, arr) => (
                      <span key={i} style={{ whiteSpace: 'nowrap' }}>{num}{i < arr.length - 1 ? ',' : ''}</span>
                    ))
                  : <span style={{ whiteSpace: 'nowrap' }}>{order.order_number}</span>}
              </span>
            </div>
          )}
          <div style={metaRow}><span style={{ color: '#555', minWidth: '110px' }}>Date & Time</span><span>{dateStr}, {timeStr}</span></div>
          <div style={metaRow}><span style={{ color: '#555', minWidth: '110px' }}>Order Type</span><span>{order.order_type === 'TAKEAWAY' ? 'Takeaway' : 'Dine-In'}</span></div>
          {order.order_type !== 'TAKEAWAY' && order.restaurant_tables && !mergedOrderNumbers?.length && (
            <div style={metaRow}><span style={{ color: '#555', minWidth: '110px' }}>Table</span><span>{order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`}</span></div>
          )}
        </div>

        {/* Items */}
        <div style={section}>
          <div style={{ display: 'flex', fontSize: '11px', fontWeight: '700', color: '#555', paddingBottom: '6px', borderBottom: '1px solid #ddd', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span style={{ flex: 1 }}>Item</span>
            <span style={{ width: '36px', textAlign: 'right', flexShrink: 0 }}>Qty</span>
            <span style={{ width: '84px', textAlign: 'right', flexShrink: 0 }}>Rate</span>
            <span style={{ width: '90px', textAlign: 'right', flexShrink: 0 }}>Amt</span>
          </div>
          {(order.order_items || []).map((oi, idx) => (
            <div key={oi.id || idx} style={{ display: 'flex', padding: '3px 0', fontSize: '12.5px' }}>
              <span style={{ flex: 1, minWidth: 0, paddingRight: '6px', wordBreak: 'break-word' }}>{oi.menu_items?.name || 'Item'}</span>
              <span style={{ width: '36px', textAlign: 'right', flexShrink: 0 }}>{oi.quantity}</span>
              <span style={{ width: '84px', textAlign: 'right', flexShrink: 0 }}>{fmt(oi.unit_price)}</span>
              <span style={{ width: '90px', textAlign: 'right', flexShrink: 0, fontWeight: '600' }}>{fmt(oi.unit_price * oi.quantity)}</span>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div style={section}>
          <div style={row}><span>Subtotal</span><span>{fmt(gross)}</span></div>

          {(order.discount_amount || 0) > 0 && (
            <div style={{ ...row, color: '#16a34a' }}>
              <span>Discount {order.discount_type === 'PERCENT' ? '' : ''}</span>
              <span>−{fmt(order.discount_amount)}</span>
            </div>
          )}

          {showTaxableRow && (
            <div style={{ ...row, color: '#555', fontSize: '12px' }}>
              <span>Taxable Amount</span><span>{fmt(order.subtotal)}</span>
            </div>
          )}

          {isGst && (
            <>
              <div style={row}>
                <span>CGST @ {halfRate}%</span><span>{fmt(cgst)}</span>
              </div>
              <div style={row}>
                <span>SGST @ {halfRate}%</span><span>{fmt(sgst)}</span>
              </div>
            </>
          )}

          {(order.service_charge_amount || 0) > 0 && (
            <div style={row}>
              <span>Service Charge @ {order.applied_service_charge_percent || ''}%</span>
              <span>{fmt(order.service_charge_amount)}</span>
            </div>
          )}

          <div style={totalRow}>
            <span>TOTAL PAYABLE</span><span>{fmt(order.total_payable)}</span>
          </div>
        </div>

        {/* Payment */}
        <div style={section}>
          {order.paid_at ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Payment: <strong>{(order.payment_method || '').toUpperCase()}</strong></span>
              <span style={{ background: '#16a34a', color: 'white', padding: '2px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '700' }}>PAID</span>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Payment pending</span>
              <span style={{ background: '#dc2626', color: 'white', padding: '2px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '700' }}>UNPAID</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ ...section, textAlign: 'center', borderBottom: 'none', color: '#555', fontSize: '11.5px' }}>
          <div style={{ fontWeight: '700', marginBottom: '2px' }}>Thank you for dining with us!</div>
          <div style={{ color: '#aaa', fontSize: '10px' }}>System generated document · {docTitle}</div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrint;
